import React, { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { Setting } from "@/domain/settingCatalog.interface";
import { parseSettingValue, settingValueToInput } from "@/utils/settingValue";

type AccountSettingValueModalProps = {
  open: boolean;
  onClose: () => void;
  setting: Setting;
  // Effective value to prefill the editor: the account override if one exists,
  // otherwise the setting's default.
  currentValue: unknown;
  hasOverride: boolean;
  onSave: (value: unknown) => Promise<void>;
  isPending: boolean;
};

const AccountSettingValueModal: FC<AccountSettingValueModalProps> = ({
  open,
  onClose,
  setting,
  currentValue,
  hasOverride,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const [raw, setRaw] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setRaw(settingValueToInput(setting.type, currentValue));
    setError("");
  }, [setting, currentValue, open]);

  const handleSave = async () => {
    const result = parseSettingValue(setting.type, raw);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    await onSave(result.value);
  };

  const isJson = setting.type === "object" || setting.type === "array";

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={`Set value — ${setting.name}`}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 360,
        maxHeight: 640,
        maxWidth: 640,
      }}
      content={
        <Box sx={{ width: "100%", pt: "12px", display: "grid", gap: "16px" }}>
          {setting.description && (
            <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
              {setting.description}
            </Typography>
          )}
          <Typography variant="bodySmall" sx={{ color: "text.secondary" }}>
            Type: {setting.type}
            {!hasOverride && " · currently using the catalog default"}
          </Typography>

          {setting.type === "boolean" ? (
            <Select
              handleChange={(value) => {
                setRaw(value as string);
                setError("");
              }}
              data={[
                { key: "true", value: "true" },
                { key: "false", value: "false" },
              ]}
              value={raw || ""}
              label="Value"
              id="account-setting-boolean"
              required
              error={!!error}
              errorMessage={error}
            />
          ) : (
            <TextField
              id="account-setting-value"
              label="Value"
              variant="outlined"
              required
              type={setting.type === "number" ? "number" : "text"}
              value={raw}
              onChange={(e) => {
                setRaw(e.target.value);
                setError("");
              }}
              error={!!error}
              helperText={error || (isJson ? "Enter valid JSON" : null)}
              multiline={isJson}
              minRows={isJson ? 4 : undefined}
              maxRows={isJson ? 10 : undefined}
              fullWidth
            />
          )}
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={handleSave}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default AccountSettingValueModal;
